import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { TRADES, tradeLabel } from "@/data/content";
import { TARIFS, formatRange, formatMonthlyRange } from "@/data/tarifs";
import { trackEvent } from "@/lib/analytics";

const FACTORS = [
  "les activités et techniques réellement exercées",
  "votre chiffre d’affaires et votre effectif",
  "votre expérience, vos diplômes et qualifications",
  "vos antécédents : sinistres, résiliation, période sans assurance",
  "les franchises et garanties complémentaires choisies",
];

export default function PriceEstimator({ initialSlug = "macon", compact = false }) {
  const [slug, setSlug] = useState(initialSlug);
  const trade = TRADES.find((t) => t.slug === slug) || TRADES[0];
  const range = TARIFS[trade.slug];

  const change = (value) => {
    setSlug(value);
    trackEvent("price_estimator_trade", { metier: value });
  };

  return (
    <div className="border-2 border-ink bg-white shadow-brutal" data-testid="price-estimator">
      <div className="grid lg:grid-cols-2">
        <div className="p-6 sm:p-8 border-b-2 lg:border-b-0 lg:border-r-2 border-ink">
          <label htmlFor="estimator-trade" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-ink/60 mb-3">
            Votre métier
          </label>
          <select
            id="estimator-trade"
            value={trade.slug}
            onChange={(e) => change(e.target.value)}
            className="w-full cursor-pointer rounded-none border-2 border-ink bg-paper px-4 py-3 text-base font-semibold outline-none focus:border-accent"
          >
            {TRADES.map((t) => (
              <option key={t.slug} value={t.slug}>{t.name}</option>
            ))}
          </select>
          {!compact && (
            <>
              <p className="mt-6 text-sm font-semibold">Ce qui fait varier votre prime :</p>
              <ul className="mt-2 space-y-1.5 text-sm leading-relaxed text-ink/75 list-disc pl-5">
                {FACTORS.map((f) => <li key={f}>{f}</li>)}
              </ul>
            </>
          )}
        </div>
        <div className="p-6 sm:p-8 bg-night text-paper" aria-live="polite">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-paper/60">Repère indicatif — {trade.name}</p>
          <p className="mt-3 font-display text-3xl font-extrabold tracking-tighter text-accent" data-testid="estimator-annual">
            {formatRange(range)} <span className="text-base font-bold text-paper/70">/ an</span>
          </p>
          <p className="mt-2 font-display text-xl font-bold tracking-tight" data-testid="estimator-monthly">
            soit environ {formatMonthlyRange(range)} <span className="text-sm font-semibold text-paper/70">/ mois</span>
          </p>
          <p className="mt-4 text-xs leading-relaxed text-paper/70">
            Prime annuelle RC Pro + décennale communiquée par le cabinet, à titre indicatif. Le tarif réel dépend de l’étude de votre dossier par l’assureur.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/devis"
              state={{ activite: trade.name }}
              onClick={() => trackEvent("price_estimator_cta", { metier: trade.slug })}
              className="inline-flex items-center gap-2 border-2 border-accent bg-accent px-6 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-paper transition-colors hover:bg-transparent"
            >
              Obtenir mon tarif exact
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to={`/metiers/${trade.slug}`}
              className="inline-flex items-center gap-2 border-2 border-paper/40 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-paper transition-colors hover:border-paper"
            >
              Fiche {tradeLabel(trade)}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
